import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Drawer, Button, Empty, Skeleton, Avatar, Typography, Image, message, Modal, Space, Spin } from 'antd';
import { ArrowLeftOutlined, ArrowRightOutlined, CloseOutlined, BellFilled, DownloadOutlined, NotificationOutlined } from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';
import ReactPlayer from 'react-player';
import Utils from '../../misc/Utils';
import Enums from '../../misc/Enums';
import PDFLessonViewer from '../../pages/LessonPage2.0/LessonComponents/PDFLessonViewer';
import './NoticeBoard.css';
import ResponsivePdfViewer from '../document-viewer/ResponsivePdfViewer';
import { getProfilePictureUrl } from '../profile/profilePictureUrl';

const { Text } = Typography;
const AVATAR_PALETTE = ['#1877F2', '#42B883', '#F59E0B', '#EF4444', '#8B5CF6', '#005ff7', '#14B8A6', '#EC4899'];

const getInitials = (value) =>
   `${value || 'N'}`
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part.charAt(0))
      .join('')
      .toUpperCase() || 'N';

const hashCode = (value) =>
   `${value || ''}`
      .split('')
      .reduce((acc, char) => (acc * 31 + char.charCodeAt(0)) >>> 0, 0);

const buildAvatarColor = (value) => AVATAR_PALETTE[hashCode(value) % AVATAR_PALETTE.length];

const normalizeFiles = (filelist) => {
   if (!filelist) return [];
   return `${filelist}`
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
};

const resolveAttachmentUrl = (itemId, fileName, subdomain) => {
   if (!fileName) return '';
   if (/^https?:\/\//i.test(fileName)) return fileName;
   return `${process.env.REACT_APP_API_S3_URL}/${subdomain}/announcementfiles/${itemId}/${fileName}`;
};

const getFileType = (fileName) => {
   const extension = `${fileName || ''}`.split('.').pop().toLowerCase();
   if (['png', 'jpg', 'jpeg', 'bmp', 'gif', 'webp'].includes(extension)) return 'image';
   if (['mp4', 'webm', 'mov'].includes(extension)) return 'video';
   if (extension === 'pdf') return 'pdf';
   return 'file';
};

// Builds an API url that streams the stored file with a Content-Disposition: attachment
// header, so the browser always downloads it instead of opening it in a new tab.
const buildDownloadApiUrl = (fileUrl, fileName) => {
   const baseUrl = process.env.REACT_APP_API_S3_URL;
   if (!fileUrl || !baseUrl) return '';

   const prefix = `${baseUrl}/`;
   if (!fileUrl.startsWith(prefix)) return '';

   const objectKey = fileUrl.slice(prefix.length).split('?')[0].split('#')[0];
   if (!objectKey) return '';

   return `${process.env.REACT_APP_LMS_API}api/AWS/download?key=${encodeURIComponent(objectKey)}&fileName=${encodeURIComponent(fileName)}`;
};

const saveBlobAsFile = (blob, fileName) => {
   const objectUrl = window.URL.createObjectURL(blob);
   const link = document.createElement('a');
   link.href = objectUrl;
   link.download = fileName || 'attachment';
   link.rel = 'noopener';
   document.body.appendChild(link);
   link.click();
   document.body.removeChild(link);
   window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);
};

const getDisplayName = (item) =>
   item.fullname ||
   item.posted_by_name ||
   item.user_name ||
   item.username ||
   item.created_by_name ||
   item.author_name ||
   item.staff_name ||
   item.title ||
   'Notice Board';

const getAuthorId = (item) => {
   const knownId = item.posted_by_id || item.postedById || item.created_by_id ||
      item.createdById || item.user_id || item.userId || item.staffid || item.staff_id ||
      item.staffId || item.staffID || item.StaffId || item.StaffID || item.created_by || item.posted_by;

   if (knownId) return knownId;

   const authorIdKey = Object.keys(item || {}).find((key) =>
      ['staffid', 'postedbyid', 'createdbyid', 'userid'].includes(key.replace(/[^a-z0-9]/gi, '').toLowerCase())
   );

   return authorIdKey ? item[authorIdKey] : null;
};

const getProfileName = (profile) => {
   if (!profile) return '';

   const name = [profile.first_name, profile.middle_name, profile.last_name]
      .filter(Boolean)
      .join(' ')
      .trim();

   return name || profile.fullname || profile.full_name || profile.name || profile.username || '';
};

const getAvatarUrl = (item, subdomain) => {
   const candidate = item.avatar || item.picture || item.profile_picture || item.profilePicture || item.user_picture;
   if (!candidate) return '';
   if (/^https?:\/\//i.test(candidate)) return candidate;
   return `${process.env.REACT_APP_API_S3_URL}/${subdomain}/${candidate}`;
};

const formatPostedAt = (value) => {
   if (!value) return 'Just now';
   const parsed = moment(value);
   if (!parsed.isValid()) return `${value}`;
   return parsed.format('MMM D, YYYY h:mm A');
};

function NoticeBoardSlide() {
   const [visible, setVisible] = useState(false);
   const [rawNotices, setRawNotices] = useState([]);
   const [noticeAuthorProfiles, setNoticeAuthorProfiles] = useState({});
   const [currentIndex, setCurrentIndex] = useState(0);
   const [loading, setLoading] = useState(false);
   const [viewerVisible, setViewerVisible] = useState(false);
   const [currentAttachmentIndex, setCurrentAttachmentIndex] = useState(0);
   const [downloadStatus, setDownloadStatus] = useState(false);
   const subdomain = localStorage.getItem('lms-subdomain');

   const notices = useMemo(() => {
      return rawNotices.map((item) => {
         const authorProfile = noticeAuthorProfiles[`${item.id}`];
         const attachments = normalizeFiles(item.filelist).map((fileName) => ({
            name: fileName,
            url: resolveAttachmentUrl(item.id, fileName, subdomain),
            type: getFileType(fileName),
         }));

         return {
            ...item,
            attachments,
            displayName: getProfileName(authorProfile) || getDisplayName(item),
            avatarUrl: authorProfile?.noticeAuthorId
               ? getProfilePictureUrl(
                  `userprofiles/${authorProfile.noticeAuthorId}/${authorProfile.noticeAuthorId}.png`,
                  subdomain,
                  Date.now()
               )
               : getAvatarUrl(item, subdomain),
            postedAt: formatPostedAt(item.start_date || item.posted_date || item.created_at),
            message: item.content || item.message || item.body || '',
         };
      });
   }, [noticeAuthorProfiles, rawNotices, subdomain]);

   const fetchNotices = useCallback(async () => {
      setLoading(true);
      try {
         Utils.verifyJwt();
         const user = Utils.getCurrentUserInfo(Enums.UserInfo.id);
         const role = Utils.getCurrentUserInfo(Enums.UserInfo.role);
         const url = `${process.env.REACT_APP_LMS_API}api/NoticeBoard/listEntityNoticeList?userid=${user}&role=${role}`;
         const response = await axios.get(url, { timeout: 10000 });
         setRawNotices(Array.isArray(response.data) ? response.data : []);
         setCurrentIndex(0);
      } catch (error) {
         console.error('Error fetching notices:', error);
         setRawNotices([]);
      } finally {
         setLoading(false);
      }
   }, []);

   useEffect(() => {
      let isCurrent = true;

      if (!rawNotices.length) {
         setNoticeAuthorProfiles({});
         return undefined;
      }

      const getAuthorProfile = async (authorId) => {
         try {
            const staffResponse = await axios.get(`${process.env.REACT_APP_API_MANAGEMENT}/getstaffinfo?id=${authorId}`, { timeout: 10000 });
            if (staffResponse.data) return staffResponse.data;
         } catch {
            // A notice can also be posted by a student account.
         }

         try {
            const studentResponse = await axios.get(`${process.env.REACT_APP_API_MANAGEMENT}/getstudentinfo?id=${authorId}`, { timeout: 10000 });
            return studentResponse.data || null;
         } catch {
            return null;
         }
      };

      const getNoticeAuthorId = async (notice) => {
         const listAuthorId = getAuthorId(notice);
         if (listAuthorId) return listAuthorId;

         try {
            const response = await axios.get(`${process.env.REACT_APP_LMS_API}api/NoticeBoard/getNotice?id=${notice.id}`, { timeout: 10000 });
            return getAuthorId(response.data || {});
         } catch {
            return null;
         }
      };

      Promise.all(rawNotices.map(async (notice) => {
         const authorId = await getNoticeAuthorId(notice);
         const profile = authorId ? await getAuthorProfile(authorId) : null;
         return [notice.id, profile ? { ...profile, noticeAuthorId: authorId } : null];
      }))
         .then((profiles) => {
            if (!isCurrent) return;

            setNoticeAuthorProfiles(profiles.reduce((result, [noticeId, profile]) => {
               if (profile) result[`${noticeId}`] = profile;
               return result;
            }, {}));
         });

      return () => {
         isCurrent = false;
      };
   }, [rawNotices]);

   useEffect(() => {
      fetchNotices();
   }, [fetchNotices]);

   useEffect(() => {
      if (visible) {
         fetchNotices();
      }
   }, [fetchNotices, visible]);

   const handleNext = () => {
      if (currentIndex < notices.length - 1) {
         setCurrentIndex(currentIndex + 1);
      }
   };

   const handlePrev = () => {
      if (currentIndex > 0) {
         setCurrentIndex(currentIndex - 1);
      }
   };

   const currentNotice = notices[currentIndex];
   const noticeCount = notices.length;
   const currentAttachmentCount = currentNotice?.attachments?.length || 0;

   useEffect(() => {
      if (!viewerVisible || !currentNotice?.attachments.length) return undefined;

      const handleKeyDown = (event) => {
         if (event.key === 'Escape') {
            setViewerVisible(false);
            return;
         }

         if (event.key === 'ArrowLeft') {
            event.preventDefault();
            goToPreviousAttachment();
            return;
         }

         if (event.key === 'ArrowRight') {
            event.preventDefault();
            goToNextAttachment();
         }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
   }, [viewerVisible, currentNotice?.attachments.length]);

   const goToPreviousAttachment = () => {
      const attachments = currentNotice?.attachments || [];
      setCurrentAttachmentIndex((current) =>
         current <= 0 ? attachments.length - 1 : current - 1
      );
   };

   const goToNextAttachment = () => {
      const attachments = currentNotice?.attachments || [];
      setCurrentAttachmentIndex((current) =>
         current >= attachments.length - 1 ? 0 : current + 1
      );
   };

   const downloadAttachment = async (attachment) => {
      if (!attachment?.url) return;

      setDownloadStatus(true);
      const fileName = `${attachment.name || 'attachment'}`.split('/').pop().split('?')[0] || 'attachment';

      try {
         let blob = null;
         const downloadApiUrl = buildDownloadApiUrl(attachment.url, fileName);

         // Preferred: stream the file through the API so the server sets
         // Content-Disposition: attachment. This works for every storage host and
         // guarantees a download instead of the browser previewing the file.
         if (downloadApiUrl) {
            try {
               const response = await axios.get(downloadApiUrl, { responseType: 'blob' });
               blob = response.data;
            } catch (apiError) {
               console.warn('API download failed, falling back to direct storage download.', apiError);
            }
         }

         // Fallback: download the file straight from storage (requires the storage
         // host to allow cross-origin reads).
         if (!blob) {
            const response = await fetch(attachment.url);
            if (!response.ok) throw new Error(`Failed to download file (${response.status})`);
            blob = await response.blob();
         }

         saveBlobAsFile(blob, fileName);
      } catch (error) {
         console.log(error?.message || error);
         message.error('Unable to download the attachment. Please try again.');
      } finally {
         setDownloadStatus(false);
      }
   };

   const renderAttachment = (attachment) => {
      if (!attachment || !attachment.type) {
         return null; // or some default UI
      }

      if (attachment.type === 'image') {
         return (
            <div className="noticeboard-media-image-frame">
               <Image
                  src={attachment.url}
                  alt={attachment.name}
                  preview={false}
                  className="noticeboard-media-image"
               />
            </div>
         );
      }

      if (attachment.type === 'video') {
         return (
            <div className="noticeboard-media-placeholder noticeboard-media-placeholder-video">
               <ReactPlayer
                  className="noticeboard-media-player"
                  controls
                  url={attachment.url}
                  width="100%"
                  height="100%"
               />
            </div>
         );
      }

      if (attachment.type === 'pdf') {
         return (
            <div className="noticeboard-media-placeholder noticeboard-media-placeholder-pdf">
               {/* <PDFLessonViewer fileUrl={attachment.url} /> */}
               <ResponsivePdfViewer
                  fileUrl={`${attachment.url}`}
                  height={"100%"}
                  pageLayout={{
                  transformSize: ({ size }) => ({
                     height: size.height - 450,
                     width: size.width + 30 }) }}
                  secureMode={true}
                  // watermarkText={`CAMPUSCLOUDPH PROPERTY`}
                  watermarkText={``}
                  showControls={false}
               />
            </div>
         );
      }

      return (
         <div className="noticeboard-media-placeholder noticeboard-media-placeholder-file">
            <span>{attachment.name}</span>
         </div>
      );
   };

   const renderMediaGallery = (item) => {
      if (!item.attachments.length) return null;

      const previewAttachments = item.attachments.length > 1 ? item.attachments.slice(0, 3) : item.attachments;
      const extraCount = item.attachments.length - previewAttachments.length;
      const galleryClassName =
         previewAttachments.length === 1
            ? 'noticeboard-media-gallery noticeboard-media-gallery-single'
            : 'noticeboard-media-gallery noticeboard-media-gallery-collage';

      return (
         <div className={galleryClassName}>
            {previewAttachments.map((attachment, index) => (
               <div
                  key={`${item.id}-${attachment.name}-${index}`}
                  className={[
                     'noticeboard-media-tile',
                     `noticeboard-media-tile-${Math.min(previewAttachments.length, 5)}`,
                     `noticeboard-media-tile-index-${index}`,
                  ].join(' ')}
                  onClick={() => {
                     setCurrentAttachmentIndex(index);
                     setViewerVisible(true);
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => {
                     if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setCurrentAttachmentIndex(index);
                        setViewerVisible(true);
                     }
                  }}
               >
                  {renderAttachment(attachment)}
                  {index === previewAttachments.length - 1 && extraCount > 0 && (
                     <div
                        className="noticeboard-media-overlay"
                        onClick={(event) => {
                           event.stopPropagation();
                           setCurrentAttachmentIndex(index);
                           setViewerVisible(true);
                        }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(event) => {
                           if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              setCurrentAttachmentIndex(index);
                              setViewerVisible(true);
                           }
                        }}
                     >
                        +{extraCount}
                     </div>
                  )}
               </div>
            ))}
         </div>
      );
   };

   const renderLoadingSkeleton = () => (
      <div className="noticeboard-slide-shell noticeboard-slide-skeleton">
         <div className="noticeboard-slide-hero noticeboard-slide-skeleton-hero">
            <div>
               <Skeleton.Input active size="small" className="noticeboard-slide-skeleton-kicker" />
               <Skeleton.Input active className="noticeboard-slide-skeleton-title" />
               <Skeleton.Input active size="small" className="noticeboard-slide-skeleton-subtitle" />
            </div>
            <div className="noticeboard-slide-skeleton-progress">
               <Skeleton.Input active className="noticeboard-slide-skeleton-number" />
               <Skeleton.Input active size="small" className="noticeboard-slide-skeleton-count" />
            </div>
         </div>

         <div className="noticeboard-slide-content">
            <article className="noticeboard-slide-card noticeboard-slide-skeleton-card">
               <div className="noticeboard-slide-card-header">
                  <div className="noticeboard-slide-author">
                     <Skeleton.Avatar active size={48} />
                     <div className="noticeboard-slide-skeleton-author-copy">
                        <Skeleton.Input active size="small" className="noticeboard-slide-skeleton-author" />
                        <Skeleton.Input active size="small" className="noticeboard-slide-skeleton-date" />
                     </div>
                  </div>
                  <Skeleton.Button active size="small" className="noticeboard-slide-skeleton-pill" />
               </div>

               <div className="noticeboard-slide-skeleton-message">
                  <Skeleton active title={false} paragraph={{ rows: 3 }} />
               </div>

               <div className="noticeboard-slide-skeleton-media">
                  <Skeleton.Image active />
                  <div className="noticeboard-slide-skeleton-media-stack">
                     <Skeleton.Image active />
                     <Skeleton.Image active />
                  </div>
               </div>
            </article>
         </div>

         <div className="noticeboard-slide-footer noticeboard-slide-skeleton-footer">
            <Skeleton.Button active className="noticeboard-slide-skeleton-nav" />
            <Skeleton.Input active size="small" className="noticeboard-slide-skeleton-footer-count" />
            <Skeleton.Button active className="noticeboard-slide-skeleton-nav" />
         </div>
      </div>
   );

   return (
      <>
         <button
            type="button"
            className="notice-board-floating-button"
            onClick={() => setVisible(true)}
            aria-label="Open notice board"
         >
            <BellFilled />
            <span className="nb-text">Notice Board</span>
            {noticeCount > 0 && <span className="nb-count">{noticeCount}</span>}
         </button>

         <Drawer
            // title={null}
            // placement="right"
            // onClose={() => setVisible(false)}
            // open={visible}
            // width={560}
            // zIndex={1000002}
            // styles={{ body: { padding: 0 } }}
            // closeIcon={<CloseOutlined />}
            // className="noticeboard-slide-drawer"
            title={
               <div className="assessment-assign-drawer-title">
                  <span className="assessment-assign-drawer-icon"><NotificationOutlined /></span>
                  <div>
                     <Text>Notice Board</Text>
                  </div>
               </div>
            }
            placement="right"
            width={560}
            className="assessment-assign-drawer"
            closable={false}
            onClose={() => setVisible(false)}
            open={visible}
            destroyOnClose
            zIndex={1000002}
            extra={
               <Space>
                  <Button className="assessment-assign-close-btn" type='default' shape='circle' icon={<CloseOutlined />} onClick={() => setVisible(false)} />
               </Space>
            }
         >
            {loading ? (
               renderLoadingSkeleton()
            ) : noticeCount === 0 ? (
               <div className="noticeboard-slide-state noticeboard-slide-state-empty">
                  <Empty description="No notices yet" />
               </div>
            ) : (
               <div className="noticeboard-slide-shell">
                  <div className="noticeboard-slide-hero">
                     <div>
                        {/* <Row>
                           <Col span={18}>
                              <span className="noticeboard-slide-kicker">Notice Board</span>
                           </Col>
                           <Col span={6}>
                              <div className="noticeboard-slide-progress">
                                 <strong>{currentIndex + 1}</strong>
                                 <span>of {noticeCount}</span>
                              </div>
                           </Col>
                        </Row> */}
                        <span className="noticeboard-slide-kicker">Notice Board</span>
                        <h2>Latest announcements</h2>
                        <p>Stay updated with school-wide notices and teacher updates.</p>
                     </div>
                     <div className="noticeboard-slide-progress">
                        <strong>{currentIndex + 1}</strong>
                        <span>of {noticeCount}</span>
                     </div>
                  </div>

                  {currentNotice && (
                     <div className="noticeboard-slide-content">
                        <article className="noticeboard-slide-card">
                           <div className="noticeboard-slide-card-header">
                              <div className="noticeboard-slide-author">
                                 <Avatar
                                    src={currentNotice.avatarUrl || undefined}
                                    style={!currentNotice.avatarUrl ? { backgroundColor: buildAvatarColor(currentNotice.displayName), flexShrink: 0 } : { flexShrink: 0 }}
                                    size={48}
                                 >
                                    {!currentNotice.avatarUrl ? getInitials(currentNotice.displayName) : null}
                                 </Avatar>
                                 <div className="noticeboard-slide-author-meta">
                                    <div className="noticeboard-slide-author-time">
                                       {currentNotice.postedAt}
                                    </div>
                                    <div className="noticeboard-slide-author-name">
                                       {currentNotice.displayName}
                                    </div>
                                 </div>
                              </div>
                              {currentAttachmentCount > 0 && (
                                 <span className="noticeboard-slide-attachment-pill">
                                    {currentAttachmentCount} attachment{currentAttachmentCount > 1 ? 's' : ''}
                                 </span>
                              )}
                           </div>

                           {currentNotice.message && (
                              <div className="noticeboard-slide-message">
                                 {currentNotice.message}
                              </div>
                           )}

                           {renderMediaGallery(currentNotice)}
                        </article>
                     </div>
                  )}

                  <div className="noticeboard-slide-footer">
                     <Button
                        className="button-shadow"
                        type="primary"
                        shape="round"
                        onClick={handlePrev}
                        disabled={currentIndex === 0}
                     >
                        <ArrowLeftOutlined />
                     </Button>
                     <Text className="noticeboard-slide-footer-counter">
                        Notice {currentIndex + 1} of {noticeCount}
                     </Text>
                     <Button
                        className="button-shadow"
                        type="primary"
                        shape="round"
                        onClick={handleNext}
                        disabled={currentIndex === noticeCount - 1}
                     >
                        <ArrowRightOutlined />
                     </Button>
                  </div>
               </div>
            )}
         </Drawer>

         {currentNotice && currentNotice.attachments.length > 0 && (
            <Modal
               title={(
                  <div className="noticeboard-preview-title">
                     <span className="noticeboard-preview-title-name">{currentNotice.displayName}</span>
                     <span className="noticeboard-preview-title-time">{currentNotice.postedAt}</span>
                  </div>
               )}
               centered
               open={viewerVisible}
               onCancel={() => setViewerVisible(false)}
               okButtonProps={{ style: { display: 'none' } }}
               width={980}
               className="noticeboard-slide-preview-modal"
               cancelText="Close"
            >
               <div className="noticeboard-modal-body">
                  {currentNotice.attachments.length > 0 && (
                     <div className="noticeboard-viewer">
                        <button
                           type="button"
                           className="noticeboard-viewer-nav noticeboard-viewer-nav-left"
                           onClick={goToPreviousAttachment}
                           aria-label="Previous attachment"
                        >
                           &#8249;
                        </button>

                        <div className="noticeboard-viewer-stage">
                           <div className="noticeboard-viewer-counter">
                              {currentAttachmentIndex + 1} / {currentNotice.attachments.length}
                           </div>
                           <button
                              type="button"
                              className="noticeboard-viewer-download"
                              onClick={() => downloadAttachment(currentNotice.attachments[currentAttachmentIndex])}
                              disabled={downloadStatus}
                              aria-label="Download attachment"
                              title="Download attachment"
                           >
                              {downloadStatus ? <Spin size="small" /> : <DownloadOutlined />}
                              <span className="noticeboard-viewer-download-label">Download</span>
                           </button>
                           {renderAttachment(currentNotice.attachments[currentAttachmentIndex])}
                        </div>

                        <button
                           type="button"
                           className="noticeboard-viewer-nav noticeboard-viewer-nav-right"
                           onClick={goToNextAttachment}
                           aria-label="Next attachment"
                        >
                           &#8250;
                        </button>
                     </div>
                  )}
               </div>
            </Modal>
         )}
      </>
   );
}

export default NoticeBoardSlide;
